import { useContext, useState, useRef } from 'react';
import { AuthCtx } from '../../context/AuthCtxProvider';
import { checkActionSubPermission } from '../../utils/common';
import { PermissionKeys, SubPermissionKeys } from '../../constants/constants';
import { useQuery, useQueryClient } from 'react-query';
import MlxButton from '../../components/common/MlxButton/MlxButton';
import SubscriptionPlanList from './subscription-plan-list/SubscriptionPlanList';
import AddOnPlanList from './addon-plan-list/AddOnPlanList';
import CreatePlan, { CreatePlanRef } from './create-plan/CreatePlan';
import withSidebar from '../../hoc/withSideBar/withSideBar';
import { TabView, TabPanel } from 'primereact/tabview';
import { _FeatureForPlan, _SubscriptionPlanFromAPI, getFeatureListForPlans, _AddonPlanFromAPI } from '../../helpers/subscriptionHelper';
import { PLAN_FEATURES_LIST } from '../../constants/queryKeys';
import { _ToastMessageProps } from '../../constants/staticTypes';
import { defaultToastData } from '../../helpers/commonHelper';
import MlxToast from '../../components/common/MlxToast/MlxToast';
import logger from '../../utils/logger';
import './Subscriptions.scss';

function Subscriptions() {
    const queryClient = useQueryClient();
    const { userType } = useContext(AuthCtx);
    const [showCreatePlan, setShowCreatePlan] = useState(false);
    const [isCreatingAddOn, setIsCreatingAddOn] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [availableFeatures, setAvailableFeatures] = useState<_FeatureForPlan[]>([]);
    const [editingPlan, setEditingPlan] = useState<_SubscriptionPlanFromAPI | null>(null);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const [isSaving, setIsSaving] = useState(false);
    const createPlanRef = useRef<CreatePlanRef>(null);

    // Fetch features list from API when page loads
    const featuresQuery = useQuery({
        queryKey: [PLAN_FEATURES_LIST],
        queryFn: () => getFeatureListForPlans(queryClient, 1, 100),
        onSuccess: (response) => {
            const featuresData = response?.data?.data?.data as _FeatureForPlan[];
            setAvailableFeatures(featuresData || []);
        },
        onError: (error) => logger.log('Failed to fetch features list', error),
        refetchOnWindowFocus: false,
    });

    const handleCreatePlan = () => {
        setEditingPlan(null);
        setIsCreatingAddOn(false);
        setShowCreatePlan(true);
    };

    const handleEditPlan = (plan: _SubscriptionPlanFromAPI | _AddonPlanFromAPI) => {
        setEditingPlan(plan as _SubscriptionPlanFromAPI);
        setIsCreatingAddOn(plan.isAddon);
        setShowCreatePlan(true);
    };

    const handleCreateAddOn = () => {
        setEditingPlan(null);
        setIsCreatingAddOn(true);
        setShowCreatePlan(true);
    };

    const handleCancelCreate = () => {
        setShowCreatePlan(false);
        setIsCreatingAddOn(false);
        setEditingPlan(null);
    };

    const handleResetPlan = () => {
        if (createPlanRef.current) {
            createPlanRef.current.handleReset();
        }
    };

    const handleUpdatePlan = () => {
        if (createPlanRef.current) {
            createPlanRef.current.handleSave();
        }
    };

    return (
        <>
            <MlxToast
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <div className="page-section">
                <span className="page-header">Subscription Management</span>
                <div className="page-content h-100">
                    {showCreatePlan ? (
                        <div className="d-flex flex-column h-100">
                            <div className="d-flex flex-column  flex-grow-0">
                                <div className="page-header-with-back d-flex align-items-center mt-2">
                                    <MlxButton
                                        rounded
                                        text
                                        size="large"
                                        icon="ri-arrow-left-line"
                                        onClick={handleCancelCreate}
                                        className="text-dark me-2"
                                    />
                                    <span className="fw-bold">
                                        {editingPlan
                                            ? isCreatingAddOn
                                                ? 'Edit Add-On'
                                                : 'Edit Subscription Plan'
                                            : isCreatingAddOn
                                              ? 'Add Add-On'
                                              : 'Add Subscription Plan'}
                                    </span>
                                </div>
                            </div>
                            <div className="d-flex flex-column flex-grow-1 overflow-auto">
                                <CreatePlan
                                    ref={createPlanRef}
                                    onCancel={handleCancelCreate}
                                    isAddOn={isCreatingAddOn}
                                    availableFeatures={availableFeatures}
                                    featuresLoading={featuresQuery.isFetching}
                                    editData={editingPlan}
                                    setToastMessage={setToastMessage}
                                    setIsSaving={setIsSaving}
                                />
                            </div>
                            <div className="d-flex flex-grow-0">
                                <div className="create-subscription-footer">
                                    <MlxButton
                                        label={editingPlan ? 'Reset' : 'Cancel'}
                                        onClick={editingPlan ? handleResetPlan : handleCancelCreate}
                                        icon={editingPlan ? 'ri-refresh-line' : 'ri-close-large-line'}
                                        iconPos="right"
                                        size="small"
                                        outlined
                                    />
                                    <MlxButton
                                        className="ms-2"
                                        label={editingPlan ? 'Update' : isCreatingAddOn ? 'Create Add-On' : 'Create Plan'}
                                        onClick={editingPlan ? handleUpdatePlan : handleUpdatePlan}
                                        icon="ri-check-line"
                                        iconPos="right"
                                        size="small"
                                        loading={isSaving}
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="subscription-management-tabs">
                                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                                    <TabPanel header="Subscription Plans">
                                        <div className="d-flex justify-content-end align-items-center mb-3 me-2">
                                            {userType === 'superAdmin' ||
                                            checkActionSubPermission(
                                                queryClient,
                                                PermissionKeys.MANAGE_STAFF,
                                                SubPermissionKeys.ADD_STAFF
                                            ) ? (
                                                <MlxButton
                                                    className="ms-2"
                                                    removeLabelOnMobile={true}
                                                    size="small"
                                                    label="Create Plan"
                                                    icon="ri-add-large-line"
                                                    iconPos="right"
                                                    onClick={handleCreatePlan}
                                                />
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    </TabPanel>
                                    <TabPanel header="Add-On Plans">
                                        <div className="d-flex justify-content-end align-items-center mb-3 me-2">
                                            {userType === 'superAdmin' ||
                                            checkActionSubPermission(
                                                queryClient,
                                                PermissionKeys.MANAGE_STAFF,
                                                SubPermissionKeys.ADD_STAFF
                                            ) ? (
                                                <MlxButton
                                                    className="ms-2"
                                                    removeLabelOnMobile={true}
                                                    size="small"
                                                    label="Create Add-On"
                                                    icon="ri-add-large-line"
                                                    iconPos="right"
                                                    onClick={handleCreateAddOn}
                                                />
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    </TabPanel>
                                </TabView>
                            </div>
                            <div className="content-body flex-grow-1 overflow-hidden">
                                {activeTab === 0 ? (
                                    <SubscriptionPlanList onEditPlan={handleEditPlan} setToastMessage={setToastMessage} />
                                ) : (
                                    <AddOnPlanList onEditPlan={handleEditPlan} setToastMessage={setToastMessage} />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default withSidebar(Subscriptions);
